// The drug / medical-service picker used by a care-plan order field.
//
// Extracted from CarePlanActivityModal's renderField so the VOICE review screen
// renders the SAME control. Before this, a dictated medicine was a plain
// TextField: no catalogue match, no OUT OF STOCK badge, no Substitute button,
// and — because __billing_charge is only written when a catalogue item is picked
// — nothing for postBillingCharges to bill. Typed and dictated orders now behave
// identically because they are literally the same component.
//
// All lookup state lives in useCarePlanServiceSearch; this file is presentation
// plus the two writes a selection makes (__billing_charge, and the medicine's
// generic_name sibling).

import { Autocomplete, Box, Button, CircularProgress, Grid, TextField, Typography } from "@mui/material";

/**
 * [BILLING] Stores the picked catalogue item on the entry as __billing_charge.
 * postBillingCharges bills only entries that carry a service_code or
 * medical_service_id, so this write is what makes an order billable at all.
 * Medication results come from search_medicines/ (SNOMED), which has no
 * service_code — those clear it back to null rather than billing a guess.
 */
export const setBillingChargeSelection = (onFieldChange, selectedItem) => {
  const serviceCode = selectedItem?.service_code ?? selectedItem?.code ?? "";
  const medicalServiceId = selectedItem?.medical_service_id ?? selectedItem?.id ?? "";
  if (serviceCode || medicalServiceId) {
    onFieldChange(["__billing_charge"], {
      service_code: serviceCode || undefined,
      name: selectedItem.name,
      // [LAB_TEST] Fields for add_patient_lab_tests/ test[] payload.
      code: serviceCode,
      medical_service_id: medicalServiceId,
      rate: selectedItem.rate ?? selectedItem.fee ?? null,
      charge_modifiable: selectedItem.chargeModifiable === true || selectedItem.charge_modifiable === true,
      quantity_modifiable: selectedItem.quantityModifiable !== false && selectedItem.quantity_modifiable !== false,
    });
    return;
  }
  onFieldChange(["__billing_charge"], null);
};

/**
 * @param {object} props
 * @param {object} props.search       useCarePlanServiceSearch() return value
 * @param {{type: string}} props.chargeSearchConfig  from getChargeSearchConfig()
 * @param {string[]} props.path       field path (where the value is written)
 * @param {string} [props.searchKey]  cache key for this field's lookup. Defaults
 *   to the joined path, which is unique within one form. The voice review shows
 *   several plans at once, all with the same internal paths, so it prefixes the
 *   plan index — otherwise two medicines would share one stock verdict.
 * @param {string} props.label
 * @param {*} props.fieldValue
 * @param {boolean} props.isRequired
 * @param {string} props.fieldError
 * @param {(path: string[], value: *) => void} props.onFieldChange
 */
export default function CarePlanServiceAutocomplete({
  search,
  chargeSearchConfig,
  path,
  searchKey: searchKeyProp,
  label,
  fieldValue,
  isRequired,
  fieldError,
  onFieldChange,
}) {
  const {
    chargeOptionsByKey,
    chargeLoadingByKey,
    medStockByKey,
    openOptionsKey,
    setOpenOptionsKey,
    scheduleChargeMasterSearch,
    fetchSubstituteOptions,
    checkMedicationStock,
    clearMedicationStock,
  } = search;

  const searchKey = searchKeyProp || path.join(".");
  const chargeItems = chargeOptionsByKey[searchKey] || [];
  // Substitutes carry a `label` (brand + composition in brackets); charge-master
  // and search_medicines items have none, so they fall back to their plain name.
  const chargeOptions = chargeItems.map((item) => item.label || item.name).filter(Boolean);
  const findChargeItem = (value) => chargeItems.find((item) => (item.label || item.name) === value);
  const isChargeSearchLoading = Boolean(chargeLoadingByKey[searchKey]);
  const isMedicationField = chargeSearchConfig.type === "MEDICATION";
  // [PHARMACY_RX] Set by checkMedicationStock once a Medication drug name is picked.
  const medStock = isMedicationField ? medStockByKey[searchKey] : null;
  const isOutOfStock = Boolean(medStock?.checked && medStock.hasStock === false);

  return (
    <Grid item xs={12} sm={6} key={searchKey}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={chargeOptions}
          loading={isChargeSearchLoading}
          value={fieldValue ?? ""}
          // Options are already the server's answer (search_medicines/,
          // search_charge_master/, or the substitute lookup). MUI's default
          // client filter matches them against the text in the box, which would
          // hide every substitute — the box still holds the out-of-stock brand.
          filterOptions={(opts) => opts}
          open={openOptionsKey === searchKey && chargeOptions.length > 0}
          onOpen={() => setOpenOptionsKey(searchKey)}
          onClose={() => setOpenOptionsKey(null)}
          onChange={(_, newValue) => {
            const selectedName = typeof newValue === "string" ? newValue : String(newValue || "");
            onFieldChange(path, selectedName);
            const matchedItem = findChargeItem(selectedName);
            setBillingChargeSelection(onFieldChange, matchedItem);
            setOpenOptionsKey(null);
            if (isMedicationField) {
              // The composition of the picked medicine, stored as a sibling of
              // the drug-name field so it lands in the same medicine object the
              // backend resolves (medication.generic_name for template 3,
              // medication_order.medication.generic_name for template 9 —
              // medicine_details() reads that key either way). Until this was
              // written, the generic was fetched for the substitute lookup and
              // then thrown away, so a care-plan medication order reached the
              // pharmacy with no generic to substitute on.
              onFieldChange([...path.slice(0, -1), "generic_name"], matchedItem?.generic_name || "");
              checkMedicationStock(searchKey, selectedName, matchedItem?.generic_name);
            }
          }}
          onInputChange={(_, value, reason) => {
            if (reason === "reset") return;
            onFieldChange(path, value);
            const matchedItem = findChargeItem(value);
            setBillingChargeSelection(onFieldChange, matchedItem || null);
            scheduleChargeMasterSearch(searchKey, value, chargeSearchConfig.type);
            if (isMedicationField) {
              // Typing over the picked drug invalidates both the badge and
              // the replay entry — otherwise choosing a store later would
              // re-check a name that is no longer in the box. The generic is
              // re-derived from whatever the text now matches, so a half-typed
              // name cannot leave the previous drug's composition behind.
              onFieldChange([...path.slice(0, -1), "generic_name"], matchedItem?.generic_name || "");
              clearMedicationStock(searchKey);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={isRequired}
              error={Boolean(fieldError)}
              helperText={fieldError || " "}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isOutOfStock && (
                      <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "#DC2626", whiteSpace: "nowrap", mr: 0.5 }}>
                        OUT OF STOCK
                      </Typography>
                    )}
                    {(isChargeSearchLoading || medStock?.checking) ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {isOutOfStock && (
          <Button
            size="small"
            onClick={() => fetchSubstituteOptions(searchKey, medStock.genericName)}
            sx={{ mt: 0.5, flexShrink: 0, whiteSpace: "nowrap", fontSize: "0.72rem", fontWeight: 700, textTransform: "none", color: "#B45309", border: "1px solid #FDE68A", backgroundColor: "#FFFBEB", borderRadius: 1, px: 1.2, py: 0.3, "&:hover": { backgroundColor: "#FEF3C7" } }}
          >
            Substitute
          </Button>
        )}
      </Box>
    </Grid>
  );
}
